/**
 * /it/trascrizione-video — video-transcription reframe bridge (Italian).
 *
 * Targets trascrizione video (900) / trascrivere video youtube (800) /
 * trascrizione video online gratis. Links to /it/trascrizione-audio pillar.
 */
import { MarketingShell } from './MarketingShell';

export function ItTrascrizioneVideo() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/trascrizione-video"
      hreflangLocales={['it']}
      documentTitle="Trascrizione video: trascrivere YouTube e riunioni gratis · Draftlize"
      documentDescription="Come trascrivere un video (anche YouTube) in testo gratis online — e come tenere le decisioni delle riunioni invece del solo testo."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Trascrizione</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Trascrivere un video —<br />
            <em>e tenere ciò che conta.</em>
          </h1>
          <p className="lede">
            Carichi la registrazione di una riunione o un video YouTube e in pochi minuti hai una trascrizione. Comodo. Ma il testo di un video, come quello di un audio, è <em>materia prima</em>. Soprattutto nei video di riunione non ti serve ogni parola, ma la <em>decisione</em> presa e il perché. Qui: come trascrivere un video gratis — e il passo dopo, in cui restano le decisioni, non solo il testo.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Trascrivere un video gratis online.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Scegli il video</strong> — registrazione di riunione, screen recording o URL di YouTube.</li>
            <li><strong>Trascrivi</strong> — uno strumento IA converte la traccia audio in testo, di solito con quota gratuita.</li>
            <li><strong>Correggi</strong> — ricontrolla nomi e termini, poi esporta.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            I video di YouTube funzionano allo stesso modo. I sottotitoli sono «testo da leggere», la trascrizione è «testo da usare». E per i video di riunione c'è un passo in più — vedi anche l'approccio di <a href="/it/trascrizione-audio" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>trascrizione audio</a>.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Sottotitoli vs decisione</span>
            <h2 className="serif">Non fermarti al testo — <em>tieni la decisione.</em></h2>
          </div>
          <p>Il tuo strumento di trascrizione e Draftlize fanno lavori diversi. Uno trasforma il video in testo; l'altro tiene attuali le decisioni al suo interno.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Trascrizione video</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Output</td><td className="partial">Sottotitoli / testo integrale</td><td className="ours yes">Schede di decisione con motivo</td></tr>
            <tr><td>Valore del video di riunione</td><td className="no">sepolto in ogni frase</td><td className="ours yes">ridotto a poche decisioni</td></tr>
            <tr><td>Tre settimane dopo</td><td className="no">troppo lungo, nessuno lo riapre</td><td className="ours yes">indirizzabile, attuale o segnalato</td></tr>
            <tr><td>Agente IA</td><td className="partial">legge solo i sottotitoli</td><td className="ours yes">legge le decisioni via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Anche trascrivendo per intero un video di riunione di un'ora, quale decisione al suo interno valga ancora, il testo non te lo dice.
        </blockquote>
        <cite>La trascrizione video la fa lo strumento. Le decisioni le tiene vive Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Quale strumento per trascrivere i video?', a: 'Uno strumento IA con supporto video converte in automatico la traccia audio in testo. Draftlize non trascrive: trasforma le decisioni nel testo in schede che restano aggiornate.' },
            { q: 'Come trascrivo un video di YouTube?', a: 'Dai l\'URL a uno strumento compatibile o esporta i sottotitoli automatici. Per video di riunione o intervista, vale la pena aggiungere il passo di conservare le decisioni.' },
            { q: 'Funziona in più lingue?', a: 'La maggior parte degli strumenti supporta più lingue. Più della lingua conta cosa resta dopo la trascrizione: le decisioni, che è ciò di cui si occupa Draftlize.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Dai video di riunione, solo le decisioni diventano schede vive <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Lascia la trascrizione video allo strumento e incolla il riassunto in Draftlize. Le decisioni della riunione diventano schede collegate e attuali — leggibili anche dal tuo agente IA.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
